export const COMPANY_DASHBOARD_STATUS_TABS = [
  'Active',
  'New',
  'LoadedForStorage',
  'InStorage',
  'LoadedForDelivery',
  'Delivered',
  'All',
  'Canceled',
] as const;

export type CompanyDashboardStatusTab = (typeof COMPANY_DASHBOARD_STATUS_TABS)[number];

export type CompanyDashboardFilters = {
  jobNumberFilter: string;
  nameFilter: string;
  statusTab: CompanyDashboardStatusTab;
};

export const DEFAULT_COMPANY_DASHBOARD_FILTERS: CompanyDashboardFilters = {
  jobNumberFilter: '',
  nameFilter: '',
  statusTab: 'Active',
};

const STORAGE_PREFIX = 'speedy.companyDashboardFilters.';

function storageKey(companyKey: string): string {
  return `${STORAGE_PREFIX}${companyKey}`;
}

function isStatusTab(value: unknown): value is CompanyDashboardStatusTab {
  return (
    typeof value === 'string' &&
    (COMPANY_DASHBOARD_STATUS_TABS as readonly string[]).includes(value)
  );
}

function isDefaultFilters(filters: CompanyDashboardFilters): boolean {
  return (
    filters.jobNumberFilter === '' &&
    filters.nameFilter === '' &&
    filters.statusTab === 'Active'
  );
}

export function parseCompanyDashboardFilters(raw: unknown): CompanyDashboardFilters {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_COMPANY_DASHBOARD_FILTERS };
  }
  const record = raw as Record<string, unknown>;
  return {
    jobNumberFilter: typeof record.jobNumberFilter === 'string' ? record.jobNumberFilter : '',
    nameFilter: typeof record.nameFilter === 'string' ? record.nameFilter : '',
    statusTab: isStatusTab(record.statusTab) ? record.statusTab : 'Active',
  };
}

export function getCompanyDashboardFilters(companyKey: string): CompanyDashboardFilters {
  try {
    const raw = sessionStorage.getItem(storageKey(companyKey));
    if (!raw) return { ...DEFAULT_COMPANY_DASHBOARD_FILTERS };
    return parseCompanyDashboardFilters(JSON.parse(raw) as unknown);
  } catch {
    return { ...DEFAULT_COMPANY_DASHBOARD_FILTERS };
  }
}

export function setCompanyDashboardFilters(
  companyKey: string,
  filters: CompanyDashboardFilters,
): void {
  try {
    const key = storageKey(companyKey);
    if (isDefaultFilters(filters)) {
      sessionStorage.removeItem(key);
      return;
    }
    sessionStorage.setItem(key, JSON.stringify(filters));
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearAllCompanyDashboardFilters(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) toRemove.push(key);
    }
    for (const key of toRemove) sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
